import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  BeakerIcon,
  CpuChipIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import AgentForm from '../components/agents/AgentForm';
import AgentCard from '../components/agents/AgentCard';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';
import { agentsAPI } from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';

const AgentConfigPage = () => {
  const [agents, setAgents] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAgent, setEditingAgent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter agents by name / description / model (case-insensitive).
  // Empty search returns all agents.
  const filteredAgents = agents.filter((a) => {
    if (!searchTerm.trim()) return true;
    const q = searchTerm.toLowerCase();
    return (
      a.name?.toLowerCase().includes(q) ||
      a.description?.toLowerCase().includes(q) ||
      a.model?.toLowerCase().includes(q)
    );
  });

  useEffect(() => {
    loadAgents();
    loadModels();
  }, []);

  const loadAgents = async () => {
    try {
      setLoading(true);
      const response = await agentsAPI.getAgents();
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error('Error loading agents:', error);
      toast.error('Failed to load agents');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const response = await agentsAPI.getAvailableModels();
      setAvailableModels(response.data.models || []);
    } catch (error) {
      console.error('Error loading models:', error);
    }
  };

  const handleCreateAgent = () => {
    setEditingAgent(null);
    setShowForm(true);
  };

  const handleEditAgent = async (agent) => {
    // The list is deliberately a summary. Fetch the authenticated detail
    // record before opening an editor so omitted fields cannot be cleared.
    try {
      const response = await agentsAPI.getAgent(agent.id);
      setEditingAgent(response.data);
      setShowForm(true);
    } catch (error) {
      console.error('Error loading agent configuration:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load agent configuration'));
    }
  };

  const handleDeleteAgent = async (agentId) => {
    try {
      await agentsAPI.deleteAgent(agentId);
      toast.success('Agent deleted successfully');
      loadAgents();
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast.error('Failed to delete agent');
    } finally {
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const handleFormSubmit = async (agentData) => {
    try {
      if (editingAgent) {
        await agentsAPI.updateAgent(editingAgent.id, agentData);
        toast.success('Agent updated successfully');
      } else {
        await agentsAPI.createAgent(agentData);
        toast.success('Agent created successfully');
      }
      setShowForm(false);
      setEditingAgent(null);
      loadAgents();
    } catch (error) {
      console.error('Error saving agent:', error);
      // Use shared helper so Pydantic 422 errors (array-shaped detail) render
      // as a readable string instead of blanking the page. See utils/apiError.js.
      toast.error(getApiErrorMessage(error, 'Failed to save agent'));
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingAgent(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <CpuChipIcon className="h-8 w-8 mr-3 text-blue-600" />
              Agent Configuration
            </h1>
            <p className="text-gray-600 mt-2">
              Configure and manage AI agents for different tasks
            </p>
          </div>
          <button
            onClick={handleCreateAgent}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Agent
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Agents</p>
              <p className="text-2xl font-bold text-gray-900">{agents.length}</p>
            </div>
            <CpuChipIcon className="h-12 w-12 text-blue-600 opacity-20" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Agents</p>
              <p className="text-2xl font-bold text-green-600">
                {agents.filter(a => a.enabled).length}
              </p>
            </div>
            <CheckCircleIcon className="h-12 w-12 text-green-600 opacity-20" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Available Models</p>
              <p className="text-2xl font-bold text-purple-600">{availableModels.length}</p>
            </div>
            <BeakerIcon className="h-12 w-12 text-purple-600 opacity-20" />
          </div>
        </div>
      </div>

      {/* Search bar — filters by name / description / model */}
      {agents.length > 0 && (
        <div className="mb-6 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search agents by name, description, or model..."
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              title="Clear search"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          )}
        </div>
      )}

      {/* Agent Form Modal */}
      {showForm && (
        <AgentForm
          agent={editingAgent}
          availableModels={availableModels}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      )}

      {/* Agents Grid */}
      {agents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CpuChipIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No agents configured</h3>
          <p className="text-gray-600 mb-4">Get started by creating your first AI agent</p>
          <button
            onClick={handleCreateAgent}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Your First Agent
          </button>
        </div>
      ) : filteredAgents.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CpuChipIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No agents match your search</h3>
          <p className="text-gray-600 mb-4">Try adjusting the search above or clear it to see all agents.</p>
          <button
            onClick={() => setSearchTerm('')}
            className="inline-flex items-center px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            <XMarkIcon className="h-5 w-5 mr-2" />
            Clear search
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              onEdit={() => handleEditAgent(agent)}
              onDelete={() => { setItemToDelete(agent); setDeleteDialogOpen(true); }}
            />
          ))}
        </div>
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => { setDeleteDialogOpen(false); setItemToDelete(null); }}
        onConfirm={() => handleDeleteAgent(itemToDelete?.id)}
        title="Delete Agent"
        entityName={itemToDelete?.name || ''}
        warningText="This will permanently delete this agent and all its configuration. This action cannot be undone."
        confirmButtonText="Delete Agent"
      />
    </div>
  );
};

export default AgentConfigPage;
